/**
 * pages/RunHistory.tsx — what an update has actually done, run by run.
 *
 * A run history exists to answer one question under pressure: *the page did not update — why?* Every
 * decision on this screen follows from that.
 *
 * **A failed run is an outcome, not an absence.** The list therefore shows every run whatever its status,
 * and a failed one is never merely red: `explainFailure` turns the runner's exception text into a sentence
 * about what went wrong, and the raw message is kept beneath it for whoever is going to the logs. The
 * translation is a courtesy layered ON the original, never a replacement for it — an error rewritten and
 * discarded is an error that cannot be searched for.
 *
 * **The counters are the diagnosis, so they are shown together and compared side by side.** "Nothing was
 * published", "the ignore lists ate the week" and "it was all read and none of it mattered" look
 * identical in a page that shows only an outcome; they are obvious the moment `commits reviewed`,
 * `dropped automatically`, `read, not reported` and `published` sit next to one another. The middle two
 * are DIFFERENT POPULATIONS and are shown apart for that reason — one is a filter, the other a
 * judgement, and a single "dropped" column could not tell a misconfiguration from a quiet week.
 *
 * **On a phone that is a card per run, with the numbers as one dense strip inside it** — the owner's
 * ruling of 2026-08-06, replacing a horizontal scroll. The argument the scroll rested on was sound and
 * is preserved rather than discarded: the objection to cards was that they give each number a row of its
 * own and stack eleven of them into a column, which is exactly what `metric` columns exist to avoid. The
 * four that form the diagnosis stay adjacent and readable at a glance.
 *
 * What the scroll did better is honest to state: scanning ONE counter DOWN many runs. That is a
 * table-shaped question, it is still answered on any screen wide enough for the table, and it was never
 * really answered on a phone either — it needed a sideways scroll to the column first, and then a
 * vertical one. Reading one run whole is the question a phone is actually holding.
 *
 * **Delivery is per destination, and a run can be half-delivered.** A Confluence page updated while Slack
 * refused is a normal, recoverable state that the run's own status cannot express, so each destination
 * carries its own row, its own status and — where there is one — the published URL. The load-bearing flag
 * is shown because it is what decided whether the watermark advanced, and therefore whether the next run
 * will cover this window again.
 *
 * **Everything above was written when a run could only be a changelog run, and most of it is
 * commit-shaped.** The counters are commits; the window is the period whose commits were read; "no
 * bullets in this run" offers two commit populations as the explanation. A DOCUMENT run has none of
 * those: it reads one file, composes one page, and — where the file was absent — commits a first draft
 * into the repository before publishing. Rendered in the changelog's columns it is a run that reviewed
 * nothing, dropped nothing, published items into no visible page and has no reason given: a successful
 * run, reported as an empty one.
 *
 * So this screen asks WHICH KIND it is looking at and answers accordingly. The kind is a property of the
 * JOB and a `ticket_runs` row does not carry it, which is why there is a third port for one field: a
 * screen that guessed the kind from zero counters would be inferring a fact from the absence of another,
 * and would call a genuinely empty changelog run a document one. The fetch is best-effort — a refusal or
 * a failure leaves the changelog reading, which is what this screen showed before there was anything
 * else to show — because the kind decides wording, never access.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { CATEGORY_ICONS, CATEGORY_LABELS, type Category } from "../../../server/changelog/types";
import { ApiError, api, type Run, type RunListResponse, type UpdateKind } from "../lib/api";
import { formatCount, formatDateTime, formatDuration, formatRelative, humanise } from "../lib/format";
import { activeRun, startManualRun } from "../lib/manualRun";
import { RunOutcome, RunProgress } from "../components/RunProgress";
import {
  Badge,
  Banner,
  Button,
  Card,
  DeniedState,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  Table,
  useToast,
  type Column,
  type Tone,
} from "../components/ui";

/* ---------------------------------------------------------------------------------------------------
 * Wire shapes, transcribed from `server/routes/runs.ts`
 * ------------------------------------------------------------------------------------------------- */

export interface RunItemView {
  readonly id: string;
  readonly position: number;
  readonly category: Category;
  readonly headline: string;
  readonly detail: string | null;
  readonly repoPath: string;
  readonly audience: "everyone" | "technical";
}

export interface DeliveryView {
  readonly id: string;
  readonly targetId: string;
  readonly kind: "confluence" | "slack";
  readonly status: "delivered" | "failed" | "skipped";
  readonly loadBearing: boolean;
  readonly detail: string | null;
  readonly url: string | null;
  readonly at: string;
}

/**
 * What a DOCUMENT run produced — its PROVENANCE, which is not what it says.
 *
 * The page itself is several thousand words and this is not where anybody reads them; the delivery row
 * alongside carries the URL of the page that is. What is here instead is the pair of facts that have
 * nowhere else to appear: whether this run RELAYED somebody's document or WROTE one, and — when it wrote
 * one — the commit sha the host reported for the push. That sha is the only place in the entire
 * application a reader can learn that Ticketless committed to their repository.
 *
 * Transcribed from `documentView` in `server/routes/runs.ts`, whose own comment says why the composed
 * body is deliberately absent from the wire.
 */
export interface RunDocumentView {
  readonly id: string;
  readonly repoPath: string;
  /** `EXECUTIVE.md` or `SECURITY.md` — the file this was read from, or written to. */
  readonly sourcePath: string;
  readonly origin: "relayed" | "generated";
  /** The blob sha as READ. Null exactly when the file was absent and this run created it. */
  readonly sourceSha: string | null;
  /** The commit sha of OUR push. Null on every relayed row. */
  readonly pushedSha: string | null;
  readonly at: string;
}

export interface RunDetail {
  readonly run: Run;
  readonly items: readonly RunItemView[];
  /**
   * Always present, and empty for a changelog run — the route sends both lists unconditionally and says
   * why: which of the two is populated is what says which kind produced this run, and a view that had to
   * know the kind before it knew what to select would be a second place the kind is interpreted.
   */
  readonly documents: readonly RunDocumentView[];
  readonly deliveries: readonly DeliveryView[];
}

const STATUS_TONE: Readonly<Record<string, Tone>> = {
  succeeded: "success",
  running: "info",
  skipped: "neutral",
  failed: "danger",
  delivered: "success",
};

function statusTone(value: string): Tone {
  return STATUS_TONE[value] ?? (value === "failed" ? "danger" : "neutral");
}

/**
 * The icon and the label for a bullet's category, from the server's vocabulary and NEVER by a bare index.
 *
 * `CATEGORY_ICONS` is a closed eight-value map and `RunItemView.category` is typed as one of its keys —
 * which is a claim about the wire, not a fact about it. Indexed directly, anything the server sends that
 * is not in the map renders `undefined`: no error, no fallback, an empty `<span>` where the icon should
 * be, and the accompanying screen-reader label empty beside it. An invisible nothing is the worst
 * possible rendering of an unexpected value, because it is indistinguishable from a bullet that was never
 * meant to carry an icon at all.
 *
 * The maps are read through a string-keyed alias so that `noUncheckedIndexedAccess` does the checking:
 * with the declared `Record<Category, string>` the compiler insists the lookup cannot fail and the `??`
 * below would be dead code it is entitled to warn about.
 */
const ICON_BY_CATEGORY: Readonly<Record<string, string>> = CATEGORY_ICONS;
const LABEL_BY_CATEGORY: Readonly<Record<string, string>> = CATEGORY_LABELS;

export function categoryIcon(category: string): string {
  return ICON_BY_CATEGORY[category] ?? "•";
}

export function categoryLabel(category: string): string {
  return LABEL_BY_CATEGORY[category] ?? humanise(category);
}

/* ---------------------------------------------------------------------------------------------------
 * Saying why, in words
 * ------------------------------------------------------------------------------------------------- */

/**
 * The runner records `err.message` verbatim, which is right for a log line and useless to a reader who
 * did not write the runner. This matches the shapes those messages actually take and says what each one
 * means for the update; anything unrecognised is passed through rather than guessed at, because a
 * confidently wrong explanation is worse than the raw text.
 */
export function explainFailure(run: Run, kind: UpdateKind = "changelog"): string | null {
  if (run.status === "skipped") {
    // The two kinds run out of things to say for different reasons, and the changelog's reason does not
    // exist for a document update: there is no window, so "nothing NEW" is not a state it can be in. The
    // one that matters is the deliberate refusal to fight — a document this update wrote once and a human
    // has since deleted is reported as nothing to publish, never written again, and `engine/runner.ts`
    // settles that as a run that did nothing on purpose rather than as a failure.
    return kind === "changelog"
      ? "This run did nothing on purpose — either another runner already held this update, or there was nothing new to report and this update does not publish empty."
      : "This run did nothing on purpose — either another runner already held this update, or there was no document to publish. A document that this update wrote once and somebody has since deleted is reported here and never written again.";
  }
  if (run.status !== "failed") return null;

  const message = (run.error ?? "").toLowerCase();

  /*
   * THE PUSH REFUSALS COME FIRST, and the ordering is load-bearing rather than tidy.
   *
   * `findWritableRepositoryForDecision` refuses with "repository '<path>' is not writable" — which the
   * destination branch below matches on "not writable" and explains as a Confluence space or a Slack
   * channel having been withdrawn. That is a confident, wrong answer sending somebody to look at the one
   * grant that is fine. Each branch here therefore requires a word only the repository-write path uses,
   * and the destination branch keeps its own wording untouched for the case it was written for.
   *
   * These are the messages `git/gitlab-write.ts`, `git/bitbucket-write.ts`, `catalogue/index.ts` and
   * `engine/kinds/document.ts` actually produce; anything else still falls through to the raw text below.
   */
  if (message.includes("repository") && message.includes("not writable")) {
    // ⚠️ There is no separate write allowlist any more (6 August 2026), and this sentence used to name
    // one. Sending an operator to look for a list that does not exist costs them the afternoon that the
    // whole `explainFailure` table exists to save. The two things that can now produce this refusal are
    // the unit's `GIT_PROJECTS` and the subject's `repo` grant — the same two that decide whether the
    // repository could have been SELECTED, which is why "it worked yesterday" is the useful hint.
    return "This update needed to write its document into the repository and was refused. Either the group granting access to that repository has changed, or the repository has dropped out of this business unit's readable set — the same one that decides which repositories this update could have been pointed at in the first place. Nothing was written and nothing was published.";
  }
  if (message.includes("does not push documents")) {
    return "This business unit has no write connector configured, so no update in it can commit a document to a repository. Relaying a document that already exists is unaffected; only the first draft of a missing one needs this.";
  }
  if (message.includes("document connector")) {
    return "This business unit has no repository document connector configured, so the file this update relays could not even be read. That is a deployment setting, not a permission — an operator adds the credential.";
  }
  if (message.includes("already exists") && message.includes("never modifies")) {
    return "The document already existed when the push was attempted, so nothing was written: Ticketless only ever creates one of these files and never modifies or overwrites it. This usually means somebody added the file between this run reading the repository and writing to it — the next run will simply relay what they wrote.";
  }
  if (message.includes("the push landed and cannot be confirmed")) {
    return "The document WAS committed to the repository, but the host did not report which commit it made, so this run stopped rather than publish a page citing a document it could not name. Nothing will be written again — the file is there — and the next run relays it.";
  }
  if (message.includes("refusing to create") || message.includes("was refused")) {
    return "The git host refused the commit that would have created this update's document. The message below is the host's own; a protected branch and a mistyped base branch are the usual causes. Nothing was published.";
  }
  if (message.includes("covers exactly one repository")) {
    return "An executive summary or a security overview is about one product, so its update covers exactly one repository. This one does not, and the run stopped rather than publish a page whose heading could only be true of one of them. Edit the update and choose a single repository.";
  }

  if (message.includes("not writable") || message.includes("not postable") || message.includes("denied")) {
    return "A destination was refused. Either the group that granted it has changed, or this business unit is no longer allowed to write to that space or channel. Nothing was published.";
  }
  if (message.includes("not configured")) {
    return "Something this update needs is not configured in this deployment — most often the model credential used to summarise commits, or the connector for a destination.";
  }
  if (message.includes("rate") || message.includes("429") || message.includes("too many")) {
    return "An upstream service asked us to slow down and kept asking for the whole run. The next scheduled run will cover this window again.";
  }
  if (
    message.includes("timeout") ||
    message.includes("timed out") ||
    message.includes("econnrefused") ||
    message.includes("enotfound") ||
    message.includes("network")
  ) {
    return "A service could not be reached — the git host, Confluence or Slack. This is usually transient; the window is not lost, and the next run covers it again.";
  }
  if (run.counters.reposFailed > 0) {
    return `${formatCount(run.counters.reposFailed)} of the repositories could not be read, and the run stopped rather than publish a changelog that silently omitted them.`;
  }
  return "The run stopped before it finished. The message below is what it recorded; the window is not lost and the next run covers it again.";
}

/* ---------------------------------------------------------------------------------------------------
 * Loading
 * ------------------------------------------------------------------------------------------------- */

export interface RunHistoryPorts {
  readonly list: (jobId: string, signal: AbortSignal) => Promise<readonly Run[]>;
  readonly detail: (runId: string, signal: AbortSignal) => Promise<RunDetail>;
  /**
   * What KIND of update these runs belong to — the one fact this screen needs and a run row does not
   * carry.
   *
   * A third request for a single field is a real cost and is worth its reason. The alternatives were
   * worse: taking the kind as a PROP means the screen is only correct when its caller happens to know it
   * — and this screen's caller does not even pass the job's NAME today, so the feature would ship dead,
   * which is the failure this repository's CLAUDE.md records having shipped twice. Inferring it from the
   * counters means reading zero commits as "must be a document", which is exactly wrong for a quiet week.
   *
   * `GET /api/jobs/:id` is the same scoped read the editor and the dashboard already make, so it adds no
   * new surface and can refuse nothing this screen was not already refused.
   */
  readonly kind: (jobId: string, signal: AbortSignal) => Promise<UpdateKind>;
}

const apiPorts: RunHistoryPorts = {
  list: async (jobId, signal) => {
    const response = await api.get<RunListResponse>(`/api/jobs/${encodeURIComponent(jobId)}/runs`, { signal });
    return response.runs;
  },
  detail: (runId, signal) => api.get<RunDetail>(`/api/runs/${encodeURIComponent(runId)}`, { signal }),
  kind: async (jobId, signal) => {
    const response = await api.get<{ readonly job: { readonly kind: UpdateKind } }>(
      `/api/jobs/${encodeURIComponent(jobId)}`,
      { signal },
    );
    return response.job.kind;
  },
};

/* ---------------------------------------------------------------------------------------------------
 * The middle of the table, which is the half that depends on the kind
 *
 * The three columns either side — when it started, how it ended, what triggered it, its trace and the way
 * into it — are true of any run. Everything between them is an answer to "what work was there to do", and
 * that question has two different answers. They are stated as two arrays rather than as one array with
 * conditional entries so that neither can quietly acquire the other's meaning: a `truncated` column, for
 * instance, means "the commit cap stopped the read" for one kind and "the model stopped mid-answer" for
 * the other, and one column serving both under one header would be a lie in whichever context it was not
 * written for.
 *
 * Hoisted out of the component because nothing here closes over its state.
 * ------------------------------------------------------------------------------------------------- */

/** The commit-shaped middle: the four counters that ARE the diagnosis, plus what came of them. */
const CHANGELOG_METRIC_COLUMNS: readonly Column<Run>[] = [
  {
    key: "windowEnd",
    header: "Window",
    type: "date",
    // Ordered on the window's END: two runs covering overlapping windows are told apart by where they
    // stopped, and a first run has no start at all.
    value: (run) => run.windowEnd,
    render: (run) =>
      run.windowEnd === null
        ? "—"
        : `${run.windowStart === null ? "start of history" : formatDateTime(run.windowStart)} → ${formatDateTime(run.windowEnd)}`,
  },
  {
    key: "duration",
    metric: true,
    shortHeader: "Took",
    header: "Took",
    type: "number",
    numeric: true,
    // A RECONSTRUCTED run has no duration to report, and the arithmetic that works for every other row
    // produces nonsense for it. `insertHistoricalRun` sets `started_at` to the DAY'S OWN CLOSE — quite
    // deliberately, because that is what this list orders by — so subtracting it from the moment the row
    // was written measures how long ago the day was, not how long the work took. A June day written up
    // in August reported "1308h 36m". There is no elapsed time to recover here: the model call that
    // produced it is billed to the live run that made it, so the honest answer is that there isn't one.
    value: (run) =>
      run.backfilled || run.finishedAt === null
        ? null
        : new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime(),
    render: (run) => (run.backfilled ? "—" : formatDuration(run.startedAt, run.finishedAt)),
  },
  {
    key: "commitsSeen",
    metric: true,
    shortHeader: "Reviewed",
    header: "Commits reviewed",
    type: "number",
    numeric: true,
    value: (run) => run.counters.commitsSeen,
    render: (run) => formatCount(run.counters.commitsSeen),
  },
  {
    key: "commitsDropped",
    metric: true,
    shortHeader: "Dropped",
    header: "Dropped automatically",
    type: "number",
    numeric: true,
    value: (run) => run.counters.commitsDropped,
    render: (run) => formatCount(run.counters.commitsDropped),
  },
  {
    // Beside "Dropped automatically" on purpose, because the pair IS the diagnosis. Both zero with no
    // items means nothing happened; the first high with the second zero means the ignore lists are
    // eating the week; the second high means it was read and there was genuinely nothing to say.
    key: "commitsRoutine",
    metric: true,
    shortHeader: "Routine",
    header: "Read, not reported",
    type: "number",
    numeric: true,
    value: (run) => run.counters.commitsRoutine,
    render: (run) => formatCount(run.counters.commitsRoutine),
  },
  {
    key: "reposCovered",
    metric: true,
    shortHeader: "Repos",
    header: "Repos covered",
    type: "number",
    numeric: true,
    value: (run) => run.counters.reposCovered,
    render: (run) => formatCount(run.counters.reposCovered),
  },
  {
    key: "reposFailed",
    metric: true,
    shortHeader: "Repos failed",
    header: "Repos failed",
    type: "number",
    numeric: true,
    value: (run) => run.counters.reposFailed,
    render: (run) => formatCount(run.counters.reposFailed),
  },
  {
    // "Items written", NOT "Items published", and the distinction is the one this screen was worst at.
    // A RECONSTRUCTED run writes its bullets to the database and delivers nothing: the page is
    // regenerated once, by the LIVE run, at the end — see `engine/publishers.ts`. So a history full of
    // reconstructed days each claiming to have "published" some items, beside a Confluence page that has
    // not changed, was the application contradicting itself in front of the reader. Nor was the word
    // ever quite right for a live run: this counts what was recorded, and delivery is a separate
    // outcome with its own failure. The tooltip below says where the bullets actually are.
    key: "itemsPublished",
    metric: true,
    shortHeader: "Items",
    header: "Items written",
    type: "number",
    numeric: true,
    value: (run) => run.counters.itemsPublished,
    render: (run) => (
      <span
        title={
          run.backfilled
            ? "Recorded for this day. Reconstructed days appear on the page when the update next publishes — they are not delivered on their own."
            : "Recorded for this run. Whether they reached each destination is the run's own outcome."
        }
      >
        {formatCount(run.counters.itemsPublished)}
      </span>
    ),
  },
  {
    key: "itemsTruncated",
    metric: true,
    shortHeader: "Hidden",
    header: "Items hidden",
    type: "number",
    numeric: true,
    value: (run) => run.counters.itemsTruncated,
    render: (run) => formatCount(run.counters.itemsTruncated),
  },
  {
    key: "truncated",
    header: "Truncated",
    type: "boolean",
    value: (run) => run.counters.truncated,
    render: (run) => (run.counters.truncated ? <Badge tone="warning">Truncated</Badge> : "No"),
  },
];

/**
 * The document-shaped middle: two counters and a warning, because there are only two facts to count.
 *
 * What is deliberately ABSENT is most of the list above. Every commit counter is structurally zero here —
 * `documentTotals` in `engine/kinds/document.ts` builds them that way, since nothing read a commit — and
 * a column of zeroes is not neutral: it reads as a run that reviewed nothing and reported nothing, which
 * is the opposite of what a successful document run did. "Repos covered" is always exactly 1 and answers
 * a question nobody has. The window is the period whose COMMITS were read, and none were.
 *
 * What is left says what this kind actually did: how long it took, how many sections the page was
 * composed into, and whether the model stopped before finishing. WHICH file, in WHICH repository, and
 * whether this run wrote it are per-document facts rather than per-run ones, so they are in the run's own
 * detail below rather than squeezed into a column.
 */
const DOCUMENT_METRIC_COLUMNS: readonly Column<Run>[] = [
  {
    key: "duration",
    metric: true,
    shortHeader: "Took",
    header: "Took",
    type: "number",
    numeric: true,
    value: (run) =>
      run.finishedAt === null ? null : new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime(),
    render: (run) => formatDuration(run.startedAt, run.finishedAt),
  },
  {
    key: "itemsPublished",
    metric: true,
    shortHeader: "Sections",
    header: "Page sections",
    type: "number",
    numeric: true,
    // The same column the changelog counts bullets in — `engine/kinds/document.ts` files `blocks.length`
    // into `items_published` — named for what it holds here. A page composed into a handful of sections
    // is the ordinary case; nought is a run that published nothing at all.
    value: (run) => run.counters.itemsPublished,
    render: (run) => (
      <span title="How many sections the page was composed into. The page itself is at the delivery's URL, below.">
        {formatCount(run.counters.itemsPublished)}
      </span>
    ),
  },
  {
    // The SAME `truncated` flag, meaning something else entirely — which is why it is a separate column
    // with its own header rather than the changelog's shared one. For a document it is the model having
    // stopped mid-answer, so part of the source is simply not on the published page.
    key: "truncated",
    header: "Cut short",
    type: "boolean",
    value: (run) => run.counters.truncated,
    render: (run) =>
      run.counters.truncated ? (
        <span title="The model stopped before the end of its answer, so part of the document is not on the page.">
          <Badge tone="warning">Cut short</Badge>
        </span>
      ) : (
        "No"
      ),
  },
];

/* ---------------------------------------------------------------------------------------------------
 * The screen
 * ------------------------------------------------------------------------------------------------- */

export interface RunHistoryProps {
  readonly buId: string;
  readonly jobId: string;
  /** Shown in the heading when the caller already knows it; the runs themselves do not carry it. */
  readonly jobName?: string;
  /** Whether to offer *Update now* — the `job_author` role in this business unit. */
  readonly canRun?: boolean;
  readonly onBack?: () => void;
  readonly ports?: RunHistoryPorts;
  /** Test seam for the manual run; the poll cadence is a seam too, so a test need not wait three seconds. */
  readonly onStartRun?: (jobId: string) => Promise<unknown>;
  readonly pollMs?: number;
  /**
   * ⚠️ There is deliberately no `selectedRunId`/`onSelectRun` pair here any more.
   *
   * It existed so the router could drive a disclosure whose content lived on this screen, and the docblock
   * it replaced quoted `04-data.md` §1.12's settling test — *a detail somebody would ever want to send to a
   * colleague is a page, because a page has an address and an expansion does not* — while this screen went
   * on not being one. `#/jobs/:id/runs/:runId` now renders `RunDetailPage`, so *Open* is an ORDINARY LINK
   * to an address and this component holds no opinion about which run is open. Reintroducing a selection
   * prop here would be rebuilding the expansion behind the page.
   */
}

/** How often the list is re-read while a run holds its lease. Idle screens never poll. */
const POLL_MS = 3000;

export function RunHistory({
  buId,
  jobId,
  jobName,
  canRun = false,
  onBack,
  ports = apiPorts,
  onStartRun = startManualRun,
  pollMs = POLL_MS,
}: RunHistoryProps): JSX.Element {
  const toast = useToast();
  const [runs, setRuns] = useState<readonly Run[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [starting, setStarting] = useState(false);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [settled, setSettled] = useState<Run | null>(null);
  /**
   * What kind of update these runs belong to.
   *
   * `changelog` until told otherwise, and that default is what every run in this application was before
   * there were three kinds — so a screen whose kind lookup is refused, fails, or is simply slower than
   * the run list shows exactly what it showed yesterday rather than something invented. The kind governs
   * wording and column choice only; nothing here is gated on it.
   */
  const [kind, setKind] = useState<UpdateKind>("changelog");
  /** The run this screen is watching, so its settlement is reported exactly once. */
  const following = useRef<string | null>(null);

  /**
   * Clearing belongs to a change of subject, not to a re-read.
   *
   * The loader below runs again on every poll, and if it blanked the list first the table would vanish and
   * return every three seconds while a run was in flight — precisely when somebody is watching it. So the
   * reset lives here, keyed on the job and the scope, and a refresh leaves the previous rows on screen
   * until the new ones land.
   */
  useEffect(() => {
    setRuns(null);
    setError(null);
    setSettled(null);
    // Back to the default with the rest of it: a kind is a fact about ONE job, and carrying the previous
    // job's answer into the next job's screen would caption one update's runs with another's shape.
    setKind("changelog");
    following.current = null;
  }, [buId, jobId]);

  /**
   * The kind, fetched once per job — NOT on the poll.
   *
   * It cannot change: `server/routes/jobs.ts` omits it from the edit schema by name and refuses a PATCH
   * that disagrees with the stored row, so re-reading it every three seconds while a run is in flight
   * would be a request per tick for an answer that is settled for the life of the update.
   *
   * The failure path is deliberately silent. This is the only call on the screen whose refusal costs
   * nothing a reader needs: the runs are already loaded by their own route, and an error banner about a
   * lookup nobody asked for would obscure the list it sits above.
   */
  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    ports
      .kind(jobId, controller.signal)
      .then((loaded) => {
        if (live) setKind(loaded);
      })
      .catch(() => undefined);

    return () => {
      live = false;
      controller.abort();
    };
  }, [ports, jobId, buId]);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    ports
      .list(jobId, controller.signal)
      .then((loaded) => {
        if (live) setRuns(loaded);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null));
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [ports, jobId, buId, attempt]);

  const reload = useCallback((): void => setAttempt((current) => current + 1), []);

  /* -------------------------------------------------------------------------------------------------
   * Publishing now
   *
   * The state of a run in flight is the SERVER'S — a `ticket_runs` row written with `status: 'running'`
   * before any work begins, whose counters climb as the run proceeds. Nothing about the progress is kept
   * in this component, which is what makes it survive a refresh, a closed tab, and a second person
   * watching the same job: the row is simply read again. A version that remembered "I pressed the button"
   * in a state variable would show nothing at all to somebody who reloaded mid-publish, while the publish
   * carried on regardless.
   * ----------------------------------------------------------------------------------------------- */

  const live = runs === null ? null : activeRun(runs);

  useEffect(() => {
    if (live !== null) {
      following.current = live.id;
      setStarting(false);
      return;
    }
    if (following.current === null || runs === null) return;
    const finished = runs.find((run) => run.id === following.current) ?? null;
    following.current = null;
    if (finished === null) return;
    setSettled(finished);
    toast.show(
      finished.status === "failed" ? "The update failed" : finished.status === "skipped" ? "Nothing to publish" : "Update published",
      {
        tone: finished.status === "failed" ? "danger" : "info",
        detail:
          finished.status === "succeeded"
            ? `${formatCount(finished.counters.itemsPublished)} ${kind === "changelog" ? "item(s)" : "page section(s)"} published in ${formatDuration(finished.startedAt, finished.finishedAt)}.`
            : (finished.error ?? "See the run below for what it reviewed."),
      },
    );
  }, [live, runs, toast, kind]);

  // Poll only while something is in flight, and only then. An idle history costs one request.
  useEffect(() => {
    if (live === null && !starting) return;
    const timer = setTimeout(reload, pollMs);
    return () => clearTimeout(timer);
  }, [live, starting, attempt, pollMs, reload]);

  const startNow = useCallback((): void => {
    setStarting(true);
    setUnavailable(null);
    setSettled(null);
    // Deliberately not awaited for the outcome. The request is synchronous on the server and may well
    // outlive this page; the row is what says whether it started, so the list is re-read at once.
    void Promise.resolve(onStartRun(jobId))
      .catch((cause: unknown) => {
        const failure =
          cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
        if (failure.status === 503) {
          // Not a refusal: this deployment has no model credential, so nothing can be summarised.
          setUnavailable(failure.userMessage);
          setStarting(false);
          return;
        }
        toast.show("The update could not be started", { tone: "danger", detail: failure.userMessage });
        setStarting(false);
      })
      .finally(reload);
    reload();
  }, [jobId, onStartRun, reload, toast]);

  const columns: readonly Column<Run>[] = [
    {
      key: "startedAt",
      header: "Started",
      type: "date",
      value: (run) => run.startedAt,
      render: (run) => <time title={formatDateTime(run.startedAt)}>{formatRelative(run.startedAt)}</time>,
    },
    {
      key: "status",
      header: "Outcome",
      type: "text",
      value: (run) => run.status,
      render: (run) => <Badge tone={statusTone(run.status)}>{humanise(run.status)}</Badge>,
    },
    {
      // "Reconstructed" is not a third trigger — the column CHECK on `trigger` could not be widened
      // without rebuilding the table — but it is the fact a reader of this list needs, because a run
      // dated three weeks ago that appeared this morning is otherwise inexplicable.
      key: "trigger",
      header: "Trigger",
      type: "text",
      value: (run) => (run.backfilled ? "reconstructed" : run.trigger),
      render: (run) => (run.backfilled ? "Reconstructed" : humanise(run.trigger)),
    },
    // The kind decides the middle of the table and nothing else about it. See the two arrays above.
    ...(kind === "changelog" ? CHANGELOG_METRIC_COLUMNS : DOCUMENT_METRIC_COLUMNS),
    {
      key: "traceId",
      header: "Trace",
      type: "text",
      value: (run) => run.traceId,
      render: (run) => <span className="mono">{run.traceId ?? "—"}</span>,
    },
    {
      key: "detail",
      header: "Detail",
      // An action column: one of the four permitted exemptions, since there is no value to order by.
      sortable: false,
      // ⚠️ AN ANCHOR, not a `Button` with an `onClick`, and no `aria-expanded` on it any more. This opens
      // an ADDRESS now (`RunDetailPage`), and a control that navigates is a link: it announces itself as
      // one, it is reachable by the browser's own affordances — middle-click, open in a new tab, copy the
      // address for a colleague, which is the whole reason this detail has an address at all — and it can
      // no longer claim to be a disclosure that expands something on this screen, because nothing does.
      // The accessible name still names its row: a bare "Open" repeated down a column tells a screen
      // reader nothing about which run it opens.
      render: (run) => (
        <a
          href={`#/jobs/${encodeURIComponent(jobId)}/runs/${encodeURIComponent(run.id)}`}
          aria-label={`Open the run that started ${formatDateTime(run.startedAt)}`}
        >
          Open
        </a>
      ),
    },
  ];

  return (
    <Page
      title="Run history"
      lede={
        jobName === undefined
          ? "Every run is recorded whether it published or not: a failed run is an outcome, not an absence."
          : `Every run of “${jobName}”, whether it published or not. A failed run is an outcome, not an absence.`
      }
      actions={
        <>
          {canRun ? (
            <Button
              variant="primary"
              onClick={startNow}
              loading={starting}
              disabled={starting || live !== null}
              title={
                live === null
                  ? "Fetch commits, summarise them and publish, without waiting for the schedule"
                  : "This update is publishing now"
              }
            >
              {live === null ? "Update now" : "Publishing…"}
            </Button>
          ) : null}
          {onBack === undefined ? null : (
            <Button variant="secondary" onClick={onBack}>
              Back to automations
            </Button>
          )}
        </>
      }
    >
      {unavailable === null ? null : (
        <Banner tone="warning">
          {unavailable} Publishing on demand needs a model credential on the server; the rest of this screen
          is unaffected, and the schedule will behave the same way until one is configured.
        </Banner>
      )}

      {live !== null ? (
        <RunProgress run={live} kind={kind} {...(jobName === undefined ? {} : { jobName })} />
      ) : settled !== null ? (
        <RunOutcome run={settled} kind={kind} />
      ) : null}

      {error !== null ? (
        error.isPermissionDenied ? (
          <DeniedState body={error.userMessage} />
        ) : (
          <ErrorState message={error.userMessage} traceId={error.traceId} onRetry={reload} />
        )
      ) : null}

      {error === null && runs === null ? <LoadingState label="Loading run history" /> : null}

      {runs !== null ? (
        <Table
          caption="Runs"
          columns={columns}
          rows={runs}
          rowKey={(run) => run.id}
          defaultSort={{ key: "startedAt", direction: "descending" }}
          mobile="cards"
          titleKey="startedAt"
          empty={{
            title: "This update has not run yet",
            body: "Runs appear here the first time the schedule fires, or as soon as somebody publishes manually. Nothing is missing.",
          }}
        />
      ) : null}
    </Page>
  );
}

/* ---------------------------------------------------------------------------------------------------
 * One run, on its own page
 * ------------------------------------------------------------------------------------------------- */

/**
 * The run's own scalar facts — and the region that only had to exist once this became a page.
 *
 * ⚠️ **Every one of these was on the LIST ROW, and a reader who arrives by address does not have the list.**
 * While the detail hung under the table, outcome / trigger / window / took / the counters / the trace were
 * one row up and repeating them would have been noise; on a page they are simply missing, and "the update
 * did not publish — why?" is not answerable without them. Losing them is the one way moving to a page could
 * have been a downgrade.
 *
 * The kind-specific middle is rendered FROM the column definitions the table uses rather than transcribed
 * beside them — `10.` in CLAUDE.md is about exactly this shape of duplicate, and a counter added to one
 * list and not the other would be invisible here rather than wrong. What is written by hand is the three
 * facts the table defines inline in its own closure.
 */
function RunFacts({ run, kind }: { readonly run: Run; readonly kind: UpdateKind }): JSX.Element {
  const middle = kind === "changelog" ? CHANGELOG_METRIC_COLUMNS : DOCUMENT_METRIC_COLUMNS;
  return (
    <Card title="This run">
      <div className="stack">
        <p>
          <strong>Outcome</strong>{" "}
          <Badge tone={statusTone(run.status)}>{humanise(run.status)}</Badge>
        </p>
        <p>
          <strong>Trigger</strong> {run.backfilled ? "Reconstructed" : humanise(run.trigger)}
        </p>
        <p>
          <strong>Started</strong>{" "}
          <time title={formatDateTime(run.startedAt)}>{formatRelative(run.startedAt)}</time>
        </p>
        {middle.map((column) => (
          <p key={column.key}>
            <strong>{column.header}</strong>{" "}
            {column.render === undefined ? String(column.value?.(run) ?? "—") : column.render(run)}
          </p>
        ))}
        <p>
          {/* Selectable text, deliberately: this is the identifier somebody quotes into a log search or
              pastes to an operator, and `06-modules.md` §5.3 R1 is explicit that one living only in a URL
              or an image is transcribed wrongly and the wrong record gets investigated. */}
          <strong>Trace</strong> <span className="mono">{run.traceId ?? "—"}</span>
        </p>
      </div>
    </Card>
  );
}

/**
 * ONE RUN, AS A PAGE — `04-data.md` §1.12's own settling test, finally applied.
 *
 * ⚠️ This was a §1.12 row expansion for five days and should never have been one. The clause is explicit
 * about where the line falls: *"A detail surface — page or drawer — is right when the detail has its own
 * actions, its own permissions, its own sub-navigation, or **enough content to need scrolling of its own**.
 * The test that settles most cases: **if the reader would ever want to send it to somebody, it is a page**,
 * because a page has an address and an expansion does not."* A failed run is precisely the thing somebody
 * sends to a colleague — `RunHistoryProps` has said so in as many words since the address was added — and
 * this detail is two tables, a provenance list and a banner. It was always a page.
 *
 * Both attempts to make an expansion work were the clause being paid for in instalments. Below the table
 * it was a screenful down and the owner reported it as a press that did nothing. Inside the card it was
 * §1.12's *"a card that expands into a second card is a detail page in denial — make it one"*, verbatim.
 * The owner asked for a modal; §1.12 rules an overlay out for this in the same breath — *"an overlay
 * covering them has spent the reader's context to show them a panel they could have had on a page"* — and
 * a page was chosen instead, on the record.
 *
 * Its own component rather than a branch inside `RunHistory`, because the two screens want different
 * things: this one has no list, so it must not poll one every three seconds, and it needs neither *Update
 * now* nor the live-run progress. What it shares is the ports and the `kind` lookup, and those are shared
 * as code rather than as a mounted component that discards most of itself.
 */
export interface RunDetailPageProps {
  readonly buId: string;
  readonly jobId: string;
  readonly runId: string;
  readonly ports?: RunHistoryPorts;
  readonly onBack?: () => void;
}

export function RunDetailPage({
  buId,
  jobId,
  runId,
  ports = apiPorts,
  onBack,
}: RunDetailPageProps): JSX.Element {
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [kind, setKind] = useState<UpdateKind>("changelog");

  // Best-effort, and silent on failure, for the reason `RunHistory`'s copy gives: the kind decides wording,
  // never access, so a refusal must leave the changelog reading rather than put a banner over a run the
  // reader is perfectly entitled to.
  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    ports
      .kind(jobId, controller.signal)
      .then((loaded) => {
        if (live) setKind(loaded);
      })
      .catch(() => undefined);
    return () => {
      live = false;
      controller.abort();
    };
  }, [ports, jobId, buId]);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setDetail(null);
    setError(null);

    ports
      .detail(runId, controller.signal)
      .then((loaded) => {
        if (live) setDetail(loaded);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null));
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [ports, runId, buId]);

  const run = detail?.run ?? null;

  return (
    // The heading names WHICH run once it is known, per §5.3 R1, and says only "Run" before that — the same
    // construction as `GoalDetail`'s. A fixed "Run detail" would be a heading that never answers the
    // question a record page exists to answer.
    <Page title={run === null ? "Run" : `Run started ${formatDateTime(run.startedAt)}`}>
      {onBack === undefined ? null : (
        <p>
          <Button variant="link" size="sm" onClick={onBack}>
            ← All runs
          </Button>
        </p>
      )}

      {error !== null ? (
        error.isPermissionDenied ? (
          <DeniedState body={error.userMessage} />
        ) : (
          <ErrorState message={error.userMessage} traceId={error.traceId} />
        )
      ) : null}

      {error === null && detail === null ? <LoadingState label="Loading this run" /> : null}

      {run === null ? null : <RunFacts run={run} kind={kind} />}
      {detail === null ? null : <RunDetailBody detail={detail} kind={kind} />}
    </Page>
  );
}

/* ---------------------------------------------------------------------------------------------------
 * One run, in full — the BODY of `RunDetailPage`, and no longer a panel hanging off a list
 * ------------------------------------------------------------------------------------------------- */

/**
 * ⚠️ Cards on a page, not one card inside another surface, and the four states are the PAGE's.
 *
 * This was a single `<Card title={`Run started …`}>` with a *Close* action, because it hung below a list
 * that was still on screen. On a page the title is the `<h1>` and repeating it on a card beneath is the
 * same fact twice in two type sizes; *Close* is a back link, which is navigation and belongs at the top;
 * and its loading and error states belonged to the panel only because the page around it was the list's.
 * So this renders sections and the page owns everything else — see `RunDetailPage`.
 */
function RunDetailBody({
  detail,
  kind,
}: {
  readonly detail: RunDetail;
  readonly kind: UpdateKind;
}): JSX.Element {
  const { run, items, documents, deliveries } = detail;
  const explanation = explainFailure(run, kind);

  const deliveryColumns: readonly Column<DeliveryView>[] = [
    { key: "kind", header: "Destination", type: "text", value: (row) => row.kind, render: (row) => humanise(row.kind) },
    {
      key: "status",
      header: "Result",
      type: "text",
      value: (row) => row.status,
      render: (row) => <Badge tone={statusTone(row.status)}>{humanise(row.status)}</Badge>,
    },
    {
      key: "loadBearing",
      header: "Load-bearing",
      type: "boolean",
      value: (row) => row.loadBearing,
      render: (row) => (row.loadBearing ? "Yes — its failure holds the watermark" : "No"),
    },
    {
      key: "url",
      header: "Published to",
      type: "text",
      value: (row) => row.url,
      render: (row) =>
        row.url === null ? (
          "—"
        ) : (
          <a href={row.url} target="_blank" rel="noreferrer noopener">
            {row.url}
          </a>
        ),
    },
    { key: "detail", header: "Detail", type: "text", value: (row) => row.detail },
    {
      key: "at",
      header: "At",
      type: "date",
      value: (row) => row.at,
      render: (row) => <time title={formatDateTime(row.at)}>{formatRelative(row.at)}</time>,
    },
  ];

  return (
    <>
      {explanation === null ? null : (
        <Banner tone={run.status === "failed" ? "danger" : "info"}>{explanation}</Banner>
      )}

      {run.error === null ? null : (
        <p className="mono">
          <span className="visually-hidden">Recorded error: </span>
          {run.error}
        </p>
      )}

      <Card title="What it published">
        {documents.length > 0 ? <DocumentProvenance documents={documents} /> : null}

        {items.length > 0 ? (
          <ul className="stack">
            {items.map((item) => (
              <li key={item.id}>
                {/* Through the guard, never a bare index: an unrecognised category rendered `undefined`
                    into both the icon and the screen-reader label, which is invisible twice over. */}
                <span aria-hidden="true">{categoryIcon(item.category)}</span>{" "}
                <span className="visually-hidden">{categoryLabel(item.category)}: </span>
                {item.headline}
                {item.detail === null ? null : ` — ${item.detail}`}{" "}
                <span className="mono">{item.repoPath}</span>
                {item.audience === "technical" ? <Badge tone="neutral">Technical</Badge> : null}
              </li>
            ))}
          </ul>
        ) : null}

        {documents.length === 0 && items.length === 0 ? (
          /*
            TWO EMPTY STATES, because the two kinds are empty for entirely different reasons and the
            changelog's explanation is meaningless for the other. "Everything in the window was dropped
            automatically" describes a filter over commits; a document run has no window and reads no
            commits, and offering that sentence would send somebody to check ignore lists that had no part
            in it. What emptiness means THERE is that the file could not be relayed and was not written —
            the run's own banner above says which of those it was.
          */
          <EmptyState
            title={kind === "changelog" ? "No bullets in this run" : "No document in this run"}
            body={
              kind === "changelog"
                ? "Either everything in the window was dropped automatically, or it was read and none of it was worth reporting, or the window held nothing at all. The two counters above say which."
                : "This run composed no page: either the file this update relays is not in the repository and could not be written, or the run stopped before it got that far. The outcome above says which, and nothing was committed to the repository."
            }
          />
        ) : null}
      </Card>

      {documents.length === 0 || items.length === 0 ? null : (
        // Both populated at once is not a state the engine produces — one handler runs per run — so if it
        // ever appears, it is worth seeing rather than silently rendering one and dropping the other.
        <Banner tone="warning">
          This run recorded both bullets and a document. That is not a state an update should reach; both
          are shown above, and this run&rsquo;s trace id in the list is what an operator needs to look
          into it.
        </Banner>
      )}

      <Card title="Where it went">
        <Table
          caption="Deliveries"
          columns={deliveryColumns}
          rows={deliveries}
          rowKey={(row) => row.id}
          defaultSort={{ key: "at", direction: "descending" }}
          titleKey="kind"
          mobile="cards"
          empty={{
            title: "Nothing was delivered",
            body: "This run produced no delivery attempt at all — it failed before publishing, or the update has no enabled destinations.",
          }}
        />
      </Card>
    </>
  );
}

/**
 * What a document run DID — and the one fact in this application that has nowhere else to appear.
 *
 * A relayed document is unremarkable: a file was read and restated onto a page. A GENERATED one is
 * Ticketless writing a file into somebody else's repository and committing it, which is the only thing
 * this application does outside its own database. That is not a detail to be inferred from a tooltip, so
 * it is a badge, the file, the repository, and the commit sha the host itself reported — never one we
 * predicted — set out plainly enough that a reader arriving from `git log` can match the two up.
 *
 * Deliberately NOT the composed page. The run detail is not where anybody reads several thousand words;
 * the delivery row below carries the URL of the page that is, and the body stays in
 * `ticket_run_documents`, which is what the publisher regenerates the page from (ADR 0004 — the page is
 * a projection of the database).
 *
 * A list rather than a table, even at more than one row, because these are the fields of one record
 * rather than a column to compare down. In practice there is exactly one: a document-kind update covers
 * exactly one repository and each kind reads exactly one file.
 */
function DocumentProvenance({
  documents,
}: {
  readonly documents: readonly RunDocumentView[];
}): JSX.Element {
  return (
    <ul className="stack">
      {documents.map((document) => (
        <li className="stack" key={document.id}>
          <span className="row">
            {document.origin === "generated" ? (
              <span title="This file was not in the repository, so this run wrote a first draft and committed it. It will never be written again: once the document exists it belongs to whoever edits it next.">
                <Badge tone="warning">Written and committed</Badge>
              </span>
            ) : (
              <span title="The file was already in the repository. It was read and restated onto the page; nothing was written to the repository.">
                <Badge tone="success">Relayed</Badge>
              </span>
            )}
            <span className="mono">{document.sourcePath}</span>
            <span className="mono">{document.repoPath}</span>
          </span>

          {document.pushedSha === null ? (
            <p className="field__hint">
              Read at revision <span className="mono">{document.sourceSha ?? "unknown"}</span>. Nothing
              was committed to this repository.
            </p>
          ) : (
            <p className="field__hint">
              Committed to <span className="mono">{document.repoPath}</span> as{" "}
              <span className="mono">{document.pushedSha}</span> — the sha the git host reported for the
              push. Ticketless will never modify or overwrite this file; from now on it relays whatever it
              says.
            </p>
          )}

          <p className="field__hint">
            <time title={formatDateTime(document.at)}>{formatRelative(document.at)}</time>
          </p>
        </li>
      ))}
    </ul>
  );
}
